(() => {
  const script = document.currentScript
  const apiOrigin = script?.dataset.experimentApi || "https://go.dtfsheet.com"
  const query = new URLSearchParams(window.location.search)
  const queryToken = query.get("dtf_exp") || ""
  const destinationUrl = new URL(window.location.href)
  const destinationConfigurationKeys = new Set(["pb", "preview_theme_id", "prototype_qa", "view"])
  for (const key of [...destinationUrl.searchParams.keys()]) {
    if (!destinationConfigurationKeys.has(key)) destinationUrl.searchParams.delete(key)
  }
  destinationUrl.searchParams.sort()
  destinationUrl.hash = ""
  const destinationKey = `${destinationUrl.origin}${destinationUrl.pathname}${destinationUrl.search}`
  const renderedPageUrl = () => {
    const pageUrl = new URL(window.location.href)
    const previewThemeId = script?.dataset.previewThemeId || ""
    if (previewThemeId && !pageUrl.searchParams.has("preview_theme_id")) pageUrl.searchParams.set("preview_theme_id", previewThemeId)
    return pageUrl.toString()
  }
  const explicitOptOut = navigator.globalPrivacyControl || navigator.doNotTrack === "1" || window.doNotTrack === "1"
  let token = explicitOptOut ? "" : queryToken
  let storedSessionId = ""
  if (queryToken) {
    query.delete("dtf_exp")
    const clean = `${window.location.pathname}${query.toString() ? `?${query}` : ""}${window.location.hash}`
    window.history.replaceState(window.history.state, "", clean)
  }
  try {
    if (explicitOptOut) {
      for (const key of ["dtf_experiment_token", "dtf_experiment_destination", "dtf_experiment_session_id", "dtf_experiment_contract"]) window.sessionStorage.removeItem(key)
    } else if (queryToken) {
      window.sessionStorage.setItem("dtf_experiment_token", queryToken)
      window.sessionStorage.setItem("dtf_experiment_destination", destinationKey)
    } else if (window.sessionStorage.getItem("dtf_experiment_destination") === destinationKey) {
      token = window.sessionStorage.getItem("dtf_experiment_token") || ""
      storedSessionId = window.sessionStorage.getItem("dtf_experiment_session_id") || ""
    }
  } catch {}
  let contract = null
  const privacyAllowed = () => !navigator.globalPrivacyControl && navigator.doNotTrack !== "1" && window.doNotTrack !== "1" && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true
  const privacyReady = new Promise(resolve => {
    if (window.Shopify?.customerPrivacy) return resolve()
    if (window.Shopify?.loadFeatures) {
      setTimeout(resolve, 2500)
      window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], resolve)
    }
    else resolve()
  })

  const post = async (path, body) => {
    const response = await fetch(`${apiOrigin}${path}`, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      keepalive: true,
      signal: AbortSignal.timeout(2500),
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`experiment_api_${response.status}`)
    return response.json()
  }

  const performancePayload = () => {
    const navigation = performance.getEntriesByType("navigation")[0]
    const paint = performance.getEntriesByType("largest-contentful-paint").at(-1)
    return {
      navigationType: navigation?.type || "navigate",
      responseStartMs: navigation?.responseStart,
      domContentLoadedMs: navigation?.domContentLoadedEventEnd,
      largestContentfulPaintMs: paint?.startTime
    }
  }

  const bootstrapPostHog = result => {
    const bootstrap = result?.posthogBootstrap
    const client = window.posthog
    if (!bootstrap || !client) return
    const projectKey = script?.dataset.posthogKey || ""
    if (projectKey && typeof client.init === "function" && !client.__loaded) {
      client.init(projectKey, {
        api_host: script?.dataset.posthogHost || "https://us.i.posthog.com",
        bootstrap,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        person_profiles: "never"
      })
    }
    if (typeof client.get_distinct_id === "function" && client.get_distinct_id() !== bootstrap.distinctID) {
      if (typeof client.reset === "function") client.reset()
      if (typeof client.identify === "function") client.identify(bootstrap.distinctID)
    }
    if (client.featureFlags?.override) client.featureFlags.override(bootstrap.featureFlags)
  }

  const expose = async () => {
    await privacyReady
    if (!token || !privacyAllowed()) return null
    const eventId = crypto.randomUUID()
    let result = null
    let failure = null
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        result = await post("/api/exposure", {
          schemaVersion: 1,
          eventId,
          experimentToken: token,
          sessionId: storedSessionId || undefined,
          occurredAt: new Date().toISOString(),
          pageUrl: renderedPageUrl(),
          performance: performancePayload()
        })
        break
      } catch (error) {
        failure = error
        if (error instanceof Error && /^experiment_api_4\d\d$/.test(error.message)) break
        if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 100 * 2 ** attempt))
      }
    }
    if (!result) throw failure || new Error("experiment_exposure_failed")
    contract = result
    try {
      window.sessionStorage.setItem("dtf_experiment_token", token)
      window.sessionStorage.setItem("dtf_experiment_session_id", result.sessionId)
      window.sessionStorage.setItem("dtf_experiment_contract", JSON.stringify(result))
    } catch {}
    bootstrapPostHog(result)
    return result
  }

  let ready = expose().catch(() => null)
  document.addEventListener("visitorConsentCollected", () => {
    if (!privacyAllowed()) contract = null
    else if (!contract) ready = expose().catch(() => null)
  })

  const nativeFetch = window.fetch.bind(window)
  window.fetch = async (input, init) => {
    const method = String(init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase()
    const target = new URL(input instanceof Request ? input.url : String(input), window.location.href)
    const cartMutation = target.origin === window.location.origin && method === "POST" && ["/cart/add.js", "/cart/update.js"].includes(target.pathname)
    if (!cartMutation || typeof init?.body !== "string") return nativeFetch(input, init)
    let body
    try {
      body = JSON.parse(init.body)
    } catch {
      return nativeFetch(input, init)
    }
    const current = contract || await ready
    if (!privacyAllowed() || !current?.cartAttributes) return nativeFetch(input, init)
    if (target.pathname === "/cart/update.js") {
      body.attributes = { ...(body.attributes || {}), ...current.cartAttributes }
    } else if (Array.isArray(body.items)) {
      body.items = body.items.map(item => ({ ...item, properties: { ...(item.properties || {}), ...current.cartAttributes } }))
    }
    return nativeFetch(input, { ...init, body: JSON.stringify(body) })
  }

  const event = async (eventName, properties = {}) => {
    const current = contract || await ready
    if (!current || !token || !privacyAllowed()) return { ok: false }
    const result = await post("/api/events", {
      schemaVersion: 1,
      eventId: crypto.randomUUID(),
      eventName,
      experimentToken: token,
      sessionId: current.sessionId,
      occurredAt: new Date().toISOString(),
      pageUrl: renderedPageUrl(),
      properties
    })
    return result
  }

  const sampleEvent = async (name, step, extra = {}) => {
    const names = {
      sample_funnel_viewed: "funnel_question_viewed",
      sample_funnel_started: "funnel_started",
      sample_question_answered: "funnel_question_answered",
      sample_upload_started: "artwork_upload_started",
      sample_upload_completed: "artwork_upload_succeeded",
      sample_upload_failed: "artwork_upload_failed",
      sample_offer_selected: "route_selected",
      sample_checkout_started: "checkout_started",
      sample_cart_failed: "client_error"
    }
    const mapped = names[name]
    if (!mapped) return { ok: false }
    const coded = value => typeof value === "string" && /^[A-Za-z0-9_.:-]{1,100}$/.test(value) ? value : undefined
    const properties = { sourceCode: "fsr12", stepId: coded(step) }
    if (name === "sample_question_answered") {
      properties.questionId = coded(extra.answerKey)
      properties.answerCode = coded(extra.answerValue)
    }
    if (name === "sample_offer_selected") properties.routeId = coded(extra.answerValue)
    if (name === "sample_checkout_started") {
      properties.itemCount = Number.isInteger(extra.properties?.cart_items) ? extra.properties.cart_items : undefined
      properties.success = true
    }
    if (name === "sample_cart_failed") properties.errorCode = "cart_handoff_failed"
    return event(mapped, properties).catch(() => ({ ok: false }))
  }

  const persistCartAttributes = async () => {
    const current = contract || await ready
    if (!privacyAllowed() || !current?.cartAttributes) return { ok: false }
    const response = await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: current.cartAttributes })
    })
    if (!response.ok) throw new Error(`experiment_cart_${response.status}`)
    return response.json()
  }

  window.DTFSExperiment = {
    get ready() { return ready },
    event,
    sampleEvent,
    cartAttributes: async () => privacyAllowed() ? ({ ...((contract || await ready)?.cartAttributes || {}) }) : ({}),
    lineItemProperties: async () => privacyAllowed() ? ({ ...((contract || await ready)?.cartAttributes || {}) }) : ({}),
    persistCartAttributes,
    posthogBootstrap: async () => ({ ...((contract || await ready)?.posthogBootstrap || {}) }),
    get: () => contract
  }
})()
